import ReportVariable from '../../../types/report/ReportVariable';
import systemVariables from '../../../infrastructure/constants/functions/systemVariables';
import { SelectSearchOption } from 'react-select-search';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import Assignment from '../../../types/functions/Assignment/Assignment';
import Lookup from '../../../types/functions/Lookup';
import Operand from '../../../types/functions/Operand/Operand';
import GlobalModuleConfig from '../../../types/report/GlobalModuleConfig';
import Mode from '../../../types/report/Mode';
import ReportConfig from '../../../types/report/ReportConfig';
import sortingHelper from '../../../infrastructure/helpers/common/sortingHelper';
import IdNameAndNumber from '../../../types/report/IdNameAndNumber';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import { getByAssignmentReferencesInGlobalConfig } from '../GlobalModule/GlobalModule.Helpers';
import { getHightestNumberInCollection } from '../../../infrastructure/helpers/common/collectionOperationHelper';
import RefDataSource from '../../../types/report/RefDataSource';

const resolveSystemVariableNameSuggestion = (variables: ReportVariable[]): string => {

    for (let i = 0; i < systemVariables.names.length; i++) {

        if (!variables.some(x => x.isSystem && x.name === systemVariables.names[i])) {

            return systemVariables.names[i];
        }
    }

    return systemVariables.names[0];
};

const prepareOptionsFromVariables = (variables: ReportVariable[]): SelectSearchOption[] => {

    let options: SelectSearchOption[] = [];
    variables.forEach((variable) => {
        options.push({
            value: variable.name,
            name: variable.name,
            items: [{ name: variable.number.toString(), value: variable.number.toString() }]
        });
    });

    return options;
}

const prepareGlobalVariables = (globalVariables: ReportVariable[], configDataSourceId: number, configVariables: ReportVariable[], dataSources: IngestionConfig[]): ReportVariable[] => {

    if (!globalVariables || globalVariables.length === 0) {
        return [];
    }

    let usedGlobalVariablesIds = configVariables.filter(x => x.mode === Mode.Global).map(x => x.globalVariableModuleId);

    let variables = globalVariables.filter(variable => !usedGlobalVariablesIds.includes(variable.id as number) &&
        (!variable.dataSourceId || (variable.dataSourceId && variable.dataSourceId === configDataSourceId)));

    variables = variables.sort((x, y) => x.number > y.number ? 1 : -1);

    let fields = dataSources.find(x => x.id === configDataSourceId)?.fieldDefinitions?.map(x => x.id as number) || [];

    let results = [] as ReportVariable[];
    variables.forEach((variable) => {

        let qualify = qualifyForOption(variable.assignment, fields);

        if (qualify) {
            results.push(variable);
        }
    });

    return results;

}
const prepareOptionsFromGlobalVariables = (globalVariables: ReportVariable[], configDataSourceId: number, configVariables: ReportVariable[], dataSources: IngestionConfig[]): SelectSearchOption[] => {

    let options: SelectSearchOption[] = [];

    let variables = prepareGlobalVariables(globalVariables, configDataSourceId, configVariables, dataSources);

    variables.forEach((variable) => {

        options.push({
            name: variable.name,
            value: variable?.id ? variable.id.toString() : '',
            items: [{ name: variable.name, value: variable?.id ? variable.id.toString() : '' }]
        });
    });

    return options;
}

const qualifyForOption = (assignment: Assignment, fieldIds: number[]): boolean => {

    let qualify = true;

    operandHelper.proceedWhitAssignment(assignment, (operand: Operand) => {
        if (qualify && operand.fieldId && !fieldIds.includes(operand.fieldId)) {
            qualify = false;
        }
    });

    return qualify;
}

const findReferencedComponentsInVariables = (globalModuleConfig: GlobalModuleConfig,
    reportConfig: ReportConfig,
    globalVariableModuleId: number,
    dataSourcesIds: Set<number>,
    referencedLookups: Lookup[],
    referencedVariables: ReportVariable[],
    referencedUdfs: UserDefinedFunction[],
    missingDataSourcesMap: Map<number, string>): void => {

    let variableNumber = globalModuleConfig.variables.find(x => x.id === globalVariableModuleId)?.number as number;

    let lookupsIds = new Set<number>(reportConfig.lookups.map(x => x.globalLookupModuleId as number));

    let variablesIds = new Set<number>(reportConfig.variables.map(x => x.globalVariableModuleId as number));

    let udfsIds = new Set<number>(reportConfig.userDefinedFunctions.map(x => x.globalUDFModuleId as number));

    getReferencesInGlobalConfig(globalModuleConfig, variableNumber, dataSourcesIds, referencedVariables, referencedLookups, referencedUdfs, lookupsIds, variablesIds, udfsIds);

    if (dataSourcesIds.size || referencedVariables.length) {

        let configDataSources: number[] = reportConfig.refDataSources.map(x => x.id);

        dataSourcesIds.forEach((dataSourceId) => {
            if (!configDataSources.includes(dataSourceId)) {
                let ds = globalModuleConfig.refDataSources.find(x => x.id === dataSourceId);

                missingDataSourcesMap.set(dataSourceId, ds?.name as string);
            }
        });
    }
}

const addCurentGlobalLookupNumbersToMap = (reportConfigLookups: Lookup[], globalLookups: Lookup[], lookupsOldAndNewNumbers: Map<number, number>) => {
    reportConfigLookups.forEach((lookup) => {
        let globalLookup = globalLookups.find(x => x.id === lookup.globalLookupModuleId);

        if (globalLookup) {
            lookupsOldAndNewNumbers.set(globalLookup.number, lookup.number);
        }
    })
}

const prepareVariables = (missingVariables: ReportVariable[], reportConfigVariables: ReportVariable[], lookupsOldAndNewNumbers: Map<number, number>, mainGlobalVariable: ReportVariable): ReportVariable[] => {

    let configVariables = copyArray(reportConfigVariables);

    let oldAndNewNumbers = new Map<number, number>();

    let updatedMissingVariables = prepareMissingVariables(missingVariables);

    let mainVariable = configVariables.find(x => x.globalVariableModuleId === mainGlobalVariable.id) as ReportVariable;

    updateMainVariableAssignment(mainVariable, mainGlobalVariable, oldAndNewNumbers);

    let newVariables = updatedMissingVariables.concat(mainVariable);

    let allVariables = configVariables.filter(x => x.globalVariableModuleId !== mainGlobalVariable.id).concat(newVariables);

    updateVariablesNumbers(allVariables);

    let variableNumbersMap = prepareMissingVariableMap(allVariables, missingVariables);

    updateNewVariablesAssignments(variableNumbersMap, newVariables, lookupsOldAndNewNumbers);

    return allVariables;
}

const updateNewVariablesAssignments = (map: Map<number, number>, variables: ReportVariable[], lookupsOldAndNewNumbers: Map<number, number>): void => {

    variables.forEach((variable) => {

        operandHelper.proceedWhitAssignment(variable.assignment, (o: Operand) => {
            if (o.variableNumber) {
                o.variableNumber = map.get(o.variableNumber) || o.variableNumber;
            }
            else if (o.lookupNumber) {
                if (lookupsOldAndNewNumbers.has(o.lookupNumber)) {

                    let currentNumber = o.lookupNumber;

                    o.lookupNumber = lookupsOldAndNewNumbers.get(o.lookupNumber) || o.lookupNumber;
                    lookupsOldAndNewNumbers.delete(currentNumber);
                }
            }
        });
    });
}

const updateVariablesNumbers = (variables: ReportVariable[]) => {
    variables.forEach((v, index) => v.number = index + 1);
}

const prepareMissingVariableMap = (variables: ReportVariable[], missingvariables: ReportVariable[]): Map<number, number> => {

    let map = new Map<number, number>();

    missingvariables.forEach((variable) => {

        let currentVarNumber = variables.find(x => x.globalVariableModuleId === variable.id)?.number || 0;

        map.set(variable.number, currentVarNumber);
    });

    return map;
}

const prepareMissingVariables = (missingVariables: ReportVariable[]): ReportVariable[] => {

    let missingVariablesCopy = copyArray(missingVariables);
    sortingHelper.sortByNumberAsc(missingVariablesCopy as IdNameAndNumber[]);

    missingVariablesCopy.forEach((variable) => {

        variable.globalVariableModuleId = variable.id as number;

        variable.id = null;

        variable.errors = [];

        variable.mode = Mode.Global;

        variable.isReadOnly = true;
    });

    return missingVariablesCopy;
}



const prepareWarningMessageOnGlobalVariableAdd = (dataSources: Map<number, string>, referencedLookups: Lookup[], referencedVariables: ReportVariable[], referencedFunctions: UserDefinedFunction[]): string => {

    let message = '';

    let missingDataSources = Array.from(dataSources.values()).map(x => `"${x}"`).join(', ');

    let missingLookups = referencedLookups.map(x => `"${x.name}"`).join(', ');

    let missingVariables = referencedVariables.map(x => `"${x.name}"`).join(', ');

    let missingFunctions = referencedFunctions.map(x => `"${x.name}"`).join(', ');

    if (missingDataSources || missingLookups || missingVariables || missingFunctions) {

        let variablesMessage = missingVariables ? `Please add variables: ${missingVariables}.\n` : '';

        let dataSourcesMessage = missingDataSources ? `Please add datasources: ${missingDataSources}.\n` : '';

        let lookupsMessage = missingLookups ? `Please add referenced Lookups ${missingLookups}.\n` : '';

        let functionssMessage = missingFunctions ? `Please add referenced Functions ${missingFunctions}.` : '';

        message = `${variablesMessage} ${dataSourcesMessage} ${lookupsMessage} ${functionssMessage}\n\n`
    }

    return message;
}


const getReferencesInGlobalConfig = (globalConfig: GlobalModuleConfig,
    number: number,
    dataSourceIds: Set<number>,
    resultVariables: ReportVariable[],
    resultLookups: Lookup[],
    resulFunctions: UserDefinedFunction[],
    existingInReportConfigLookupsIds: Set<number>,
    existingInReportConfigVariablesIds: Set<number>,
    existingInReportConfigUdfsIds: Set<number>,
): void => {

    let variable = globalConfig.variables.find(x => x.number === number);

    if (variable?.assignment) {
        getByAssignmentReferencesInGlobalConfig(globalConfig,
            variable.assignment,
            resultLookups,
            resulFunctions,
            dataSourceIds,
            existingInReportConfigLookupsIds,
            existingInReportConfigUdfsIds,
            existingInReportConfigVariablesIds,
            resultVariables
        );
    }
}

const updateMainVariableAssignment = (mainVariable: ReportVariable, mainGlobalVariable: ReportVariable, oldAndNewNumbers: Map<number, number>) => {
    mainVariable.assignment = mainGlobalVariable.assignment;
    operandHelper.proceedWhitAssignment(mainVariable.assignment, (operand: Operand) => {
        if (operand.variableNumber as number > 0 && oldAndNewNumbers.has(operand.variableNumber as number)) {

            operand.variableNumber = oldAndNewNumbers.get(operand.variableNumber as number) as number;
        }
    });
}

const dataTypeChangeMessage = 'Changing the data type will reset the logic for this variable. Please copy the logic to a new variable before continuing.';

export {
    resolveSystemVariableNameSuggestion,
    prepareOptionsFromVariables,
    dataTypeChangeMessage,
    prepareOptionsFromGlobalVariables,
    prepareWarningMessageOnGlobalVariableAdd,
    getReferencesInGlobalConfig,
    prepareMissingVariables,
    prepareVariables,
    findReferencedComponentsInVariables,
    addCurentGlobalLookupNumbersToMap,
    prepareGlobalVariables
};
